import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined'
import {
  Avatar,
  Badge,
  Button,
  Container,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet } from 'react-router'
import { useAuthStore } from '../hooks/useAuthStore'
import {
  useReceivedInvitations,
  useSentInvitations
} from '../hooks/useQueries/useCompanies'
import { useUser } from '../hooks/useQueries/useUsers'
import { getAvatarName } from '../utils/user.utils'

const avatarStyles = {
  width: '4rem',
  height: '4rem',
  margin: '0 auto',
  borderRadius: '.25rem'
}

const tabStyles = {
  textTransform: 'none',
  padding: '0.25rem 1rem',
  borderRadius: '1rem',
  minHeight: '2.625rem',
  '&.active': {
    color: 'primary.main',
    backgroundColor: 'rgba(159, 168, 218, 0.22)'
  }
}

export default function AccountLayout() {
  const { email, userId } = useAuthStore()
  const { t } = useTranslation()

  const { data: user, isLoading: isLoadingUser } = useUser(userId!)
  const { data: receivedInvitations, isLoading: isReceivedInvitationsLoading } =
    useReceivedInvitations(userId!)
  const { data: sentInvitations, isLoading: isSentInvitationsLoading } =
    useSentInvitations(userId!)

  if (
    isLoadingUser ||
    isReceivedInvitationsLoading ||
    isSentInvitationsLoading ||
    !user
  ) {
    return (
      <Container maxWidth='lg' disableGutters>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          marginBottom='3rem'
        >
          <Stack direction='row' alignItems='center' gap='1rem'>
            <Skeleton
              variant='rectangular'
              width='4rem'
              height='4rem'
              sx={{
                borderRadius: '.25rem',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }}
            />
            <Stack direction='column' gap='0.25rem'>
              <Skeleton
                variant='text'
                width='12rem'
                height='1.5rem'
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
              />
              <Skeleton
                variant='text'
                width='15rem'
                height='1.25rem'
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
              />
              <Skeleton
                variant='rectangular'
                width='6rem'
                height='1.5rem'
                sx={{
                  borderRadius: '.25rem',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }}
              />
            </Stack>
          </Stack>

          <Stack direction='row' gap='1rem'>
            <Skeleton
              variant='rectangular'
              width='8rem'
              height='2.625rem'
              sx={{ borderRadius: '1rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            />
            <Skeleton
              variant='rectangular'
              width='10rem'
              height='2.625rem'
              sx={{ borderRadius: '1rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            />
          </Stack>

          <Skeleton
            variant='rectangular'
            width='12rem'
            height='2.5rem'
            sx={{ borderRadius: '.5rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
          />
        </Stack>

        <Skeleton
          variant='rectangular'
          width='100%'
          height='40rem'
          sx={{ borderRadius: '1rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
        />
      </Container>
    )
  }

  return (
    <Container maxWidth='lg' disableGutters>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        marginBottom='3rem'
      >
        <Stack direction='row' alignItems='center' gap='1rem'>
          {user.avatarUri ? (
            <Avatar variant='rounded' src={user.avatarUri} sx={avatarStyles} />
          ) : (
            <Avatar variant='rounded' sx={avatarStyles}>
              {getAvatarName(user.name!, user.lastName!)}
            </Avatar>
          )}
          <Stack direction='column' gap='0.25rem'>
            <Typography variant='h6'>
              {user.name} {user.lastName}
            </Typography>
            <Typography variant='body1'>{email}</Typography>
            <Button
              component={Link}
              to='/profile'
              variant='text'
              size='small'
              sx={{
                textTransform: 'none',
                padding: '0.25rem 0',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  color: 'primary.dark',
                  backgroundColor: 'transparent'
                }
              }}
            >
              {t('account.edit_profile')}
            </Button>
          </Stack>
        </Stack>

        <Tabs value={false}>
          <Tab
            component={NavLink}
            to='/companies'
            icon={<GridViewOutlinedIcon />}
            iconPosition='start'
            label={t('account.companies')}
            sx={tabStyles}
          />

          <Tab
            component={NavLink}
            to='/invitations'
            icon={<MailOutlineOutlinedIcon />}
            iconPosition='start'
            label={
              <Stack direction='row' alignItems='center' gap='1rem'>
                <span>{t('account.invitations')}</span>
                <Badge
                  color='primary'
                  badgeContent={
                    (receivedInvitations?.length || 0) +
                    (sentInvitations?.length || 0)
                  }
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#C5CAE9'
                    }
                  }}
                />
              </Stack>
            }
            sx={tabStyles}
          />
        </Tabs>

        <Button
          component={Link}
          to='/companies/directory'
          variant='outlined'
          color='primary'
          startIcon={<ExploreOutlinedIcon />}
          sx={{
            textTransform: 'none',
            padding: '0.375rem 1rem',
            background: 'rgba(178, 223, 219, 0.1)'
          }}
        >
          {t('account.search_companies')}
        </Button>
      </Stack>

      <Outlet />
    </Container>
  )
}
